import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { authAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeOff, Clock, XCircle, Mail, RefreshCw, Phone, ArrowRight, ChevronDown, ChevronUp, Shield } from "lucide-react";
import { BrandLogoFull } from "../components/BrandLogo";

// ---- OTP Phone Login Section (Primary) ----
function PhoneOTPLogin({ onSuccess }) {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [step, setStep] = useState("phone"); // phone | otp | name
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const { loginWithOTP } = useAuth();
  const timerRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startResendTimer = () => {
    setResendTimer(30);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length !== 10) {
      toast.error("10-digit mobile number daalo");
      return;
    }
    setLoading(true);
    try {
      await authAPI.sendOTP(cleaned);
      toast.success("OTP sent! Check your phone");
      setStep("otp");
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP bhejne mein problem hui");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length < 4) { toast.error("Valid OTP daalo"); return; }
    setLoading(true);
    try {
      const result = await loginWithOTP(phone.replace(/\D/g, ""), otp, name || undefined);
      if (result.needs_name) {
        setStep("name");
        setLoading(false);
        return;
      }
      toast.success(result.is_new_user ? "Account created! Welcome!" : "Welcome back!");
      onSuccess(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP verify nahi hua");
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Apna naam daalo"); return; }
    setLoading(true);
    try {
      const result = await loginWithOTP(phone.replace(/\D/g, ""), otp, name.trim());
      if (result.needs_name) {
        toast.error("Please enter your name");
        setLoading(false);
        return;
      }
      toast.success("Account created! Welcome!");
      onSuccess(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await authAPI.resendOTP(phone.replace(/\D/g, ""));
      toast.success("OTP resent!");
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed");
    }
  };

  if (step === "name") {
    return (
      <form onSubmit={handleNameSubmit} className="space-y-4">
        <div className="text-center mb-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full mb-3">
            <Shield className="w-3.5 h-3.5 text-green-400" />
            <span className="text-xs font-medium text-green-400">OTP Verified</span>
          </div>
          <p className="text-sm text-muted-foreground">First time? Enter your name to get started</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Your Name</Label>
          <Input
            type="text" placeholder="e.g. Rahul Sharma" value={name}
            onChange={(e) => setName(e.target.value)} autoFocus
            className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl"
            data-testid="otp-name-input"
          />
        </div>
        <Button type="submit" disabled={loading}
          className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
          data-testid="otp-name-submit-btn">
          {loading ? <div className="spinner" /> : "Create Account"}
        </Button>
        <button type="button" onClick={() => { setStep("phone"); setOtp(""); }}
          className="w-full text-xs text-muted-foreground hover:text-foreground text-center"
          data-testid="otp-change-phone-btn">
          Change phone number
        </button>
      </form>
    );
  }

  if (step === "otp") {
    return (
      <form onSubmit={handleVerifyOTP} className="space-y-4">
        <div className="text-center mb-2">
          <p className="text-sm text-muted-foreground">
            OTP sent to <span className="text-brand-blue font-semibold">+91 {phone.replace(/\D/g, "")}</span>
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Enter OTP</Label>
          <Input
            type="text" inputMode="numeric" maxLength={6} placeholder="Enter 6-digit OTP"
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} autoFocus
            className="h-14 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl text-center text-xl tracking-[0.5em] font-mono"
            data-testid="otp-code-input"
          />
        </div>
        <Button type="submit" disabled={loading}
          className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
          data-testid="otp-verify-btn">
          {loading ? <div className="spinner" /> : (
            <>Verify & Login <ArrowRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => { setStep("phone"); setOtp(""); }}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="otp-change-number-btn">
            Change number
          </button>
          {resendTimer > 0 ? (
            <span className="text-xs text-muted-foreground">Resend in {resendTimer}s</span>
          ) : (
            <button type="button" onClick={handleResend}
              className="text-xs text-brand-blue hover:underline flex items-center gap-1"
              data-testid="otp-resend-btn">
              <RefreshCw className="w-3 h-3" /> Resend OTP
            </button>
          )}
        </div>
      </form>
    );
  }

  // Step: phone
  return (
    <form onSubmit={handleSendOTP} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Phone Number</Label>
        <div className="flex gap-2">
          <div className="flex items-center justify-center h-12 px-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-sm text-muted-foreground font-mono shrink-0">
            +91
          </div>
          <Input
            type="tel" inputMode="numeric" maxLength={10} placeholder="Enter 10-digit number"
            value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} autoFocus
            className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl font-mono"
            data-testid="otp-phone-input"
          />
        </div>
      </div>
      <Button type="submit" disabled={loading || phone.replace(/\D/g, "").length !== 10}
        className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
        data-testid="otp-send-btn">
        {loading ? <div className="spinner" /> : (
          <>
            <Phone className="w-4 h-4 mr-2" /> Send OTP
          </>
        )}
      </Button>
    </form>
  );
}

// ---- Email Login Section (Secondary) ----
function EmailLogin({ onSuccess, onStatusChange }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error("Enter your email"); return; }
    setForgotLoading(true);
    try {
      const res = await authAPI.forgotPassword(forgotEmail);
      if (res.data.preview_password) {
        toast.success(`Password reset to: ${res.data.preview_password}`);
      } else {
        toast.success("Check your email for reset instructions.");
      }
      setShowForgotPw(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      const trainerData = await login(email, password);
      toast.success("Welcome back!");
      onSuccess(trainerData);
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      if (message.includes("pending")) onStatusChange("pending");
      else if (message.includes("rejected")) onStatusChange("rejected");
      else if (message.includes("suspended")) onStatusChange("suspended");
      else if (message.includes("deactivated")) onStatusChange("deactivated");
      else if (message.includes("verify your email")) onStatusChange("unverified");
      else toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Email</Label>
          <Input id="email" type="email" placeholder="coach@example.com" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl"
            data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Password</Label>
          <div className="relative">
            <Input id="password" type={showPassword ? "text" : "password"} placeholder="Enter your password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 pr-12 rounded-xl"
              data-testid="login-password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="toggle-password-visibility">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <button type="button" onClick={() => setShowForgotPw(true)} className="text-xs text-brand-blue hover:underline" data-testid="forgot-password-link">
            Forgot password?
          </button>
        </div>
        <Button type="submit" disabled={loading}
          className="w-full h-12 bg-white/[0.06] hover:bg-white/[0.1] text-foreground font-semibold tracking-wide uppercase rounded-xl border border-white/[0.08]"
          data-testid="login-submit-btn">
          {loading ? <div className="spinner" /> : "Sign In with Email"}
        </Button>
      </form>

      <Dialog open={showForgotPw} onOpenChange={setShowForgotPw}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-tight">Reset Password</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">Enter your email and we&apos;ll send reset instructions.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-2">
            <Input value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} type="email" placeholder="your@email.com" className="h-12 bg-secondary/50 border-transparent" data-testid="forgot-email-input" />
            <Button type="submit" disabled={forgotLoading} className="w-full h-12 btn-premium text-white font-semibold uppercase rounded-xl" data-testid="forgot-submit-btn">
              {forgotLoading ? <div className="spinner" /> : "Reset Password"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---- Unverified Email Screen ----
function UnverifiedScreen({ email, onBack }) {
  const [resending, setResending] = useState(false);
  const handleResend = async () => {
    setResending(true);
    try {
      const res = await authAPI.resendVerification(email);
      toast.success(res.data.message || "Verification email resent!");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to resend email"); }
    finally { setResending(false); }
  };
  return (
    <Card className="glass-card">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6"><Mail className="w-8 h-8 text-primary" /></div>
        <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3" data-testid="unverified-title">Verify Your Email</h2>
        <p className="text-muted-foreground mb-4">Please verify your email address before logging in.</p>
        <div className="space-y-3">
          <Button onClick={handleResend} disabled={resending} className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl" data-testid="login-resend-verification-btn">
            {resending ? <div className="spinner" /> : <><RefreshCw className="w-4 h-4 mr-2" />Resend Verification Email</>}
          </Button>
          <Button onClick={onBack} variant="outline" className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10" data-testid="unverified-back-btn">Back to Login</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---- Status Screens ----
function StatusScreen({ status, email, onBack }) {
  if (status === "suspended") {
    return (
      <Card className="glass-card">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6"><XCircle className="w-8 h-8 text-destructive" /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3">Account Suspended</h2>
          <p className="text-muted-foreground mb-4">Your account has been suspended.</p>
          <div className="p-4 bg-white/[0.03] rounded-xl mb-6 space-y-3">
            <p className="text-sm text-muted-foreground">If you believe this is a mistake, please contact:</p>
            <a href="mailto:support@fittrackcoach.in" className="text-sm text-[#00BFFF] font-semibold hover:underline">support@fittrackcoach.in</a>
          </div>
          <Button onClick={onBack} variant="outline" className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10" data-testid="try-again-btn">Try Another Account</Button>
        </CardContent>
      </Card>
    );
  }
  if (status === "deactivated") {
    return (
      <Card className="glass-card">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-6"><XCircle className="w-8 h-8 text-orange-500" /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3" data-testid="deactivated-title">Account Deactivated</h2>
          <p className="text-muted-foreground mb-4">Your account has been deactivated.</p>
          <Button onClick={onBack} variant="outline" className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10" data-testid="deactivated-try-again-btn">Try Another Account</Button>
        </CardContent>
      </Card>
    );
  }
  if (status === "unverified") {
    return <UnverifiedScreen email={email} onBack={onBack} />;
  }
  if (status === "pending") {
    return (
      <Card className="glass-card">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6"><Clock className="w-8 h-8 text-yellow-500" /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3">Account Under Review</h2>
          <p className="text-muted-foreground mb-6">Your account is being set up. Please try again in a few minutes.</p>
          <Button onClick={onBack} variant="outline" className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10" data-testid="try-again-btn">Try Another Account</Button>
        </CardContent>
      </Card>
    );
  }
  if (status === "rejected") {
    return (
      <Card className="glass-card">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6"><XCircle className="w-8 h-8 text-destructive" /></div>
          <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3">Access Denied</h2>
          <p className="text-muted-foreground mb-6">Unfortunately, your account access has been denied. Please contact support.</p>
          <Button onClick={onBack} variant="outline" className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10" data-testid="try-again-btn">Try Another Account</Button>
        </CardContent>
      </Card>
    );
  }
  return null;
}

// ---- Main Login Page ----
export default function LoginPage() {
  const [accessStatus, setAccessStatus] = useState(null);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const navigate = useNavigate();
  const otpEnabled = process.env.REACT_APP_OTP_ENABLED === "true";

  const handleLoginSuccess = (trainerData) => {
    if (trainerData.is_admin) {
      navigate("/admin");
    } else {
      navigate("/dashboard");
    }
  };

  if (accessStatus) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="relative z-10 w-full max-w-md animate-in">
          <div className="flex justify-center mb-8"><BrandLogoFull className="h-24" /></div>
          <StatusScreen status={accessStatus} onBack={() => setAccessStatus(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 noise-overlay" style={{background: 'linear-gradient(180deg, #0B0F1A 0%, #020617 100%)'}}>
      <div className="relative z-10 w-full max-w-md animate-in">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <BrandLogoFull className="h-28 animate-pulse-glow" />
        </div>

        <Card className="glass-card">
          <CardHeader className="space-y-1 text-center pb-4">
            <CardTitle className="font-heading text-2xl tracking-tight uppercase">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {otpEnabled ? "Login with your phone number" : "Sign in to manage your clients"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">

            {otpEnabled ? (
              <>
                {/* PRIMARY: Phone OTP Login */}
                <div className="relative" data-testid="phone-otp-section">
                  <div className="absolute -inset-[1px] bg-gradient-to-r from-brand-blue/30 via-primary/20 to-brand-blue/30 rounded-2xl blur-sm" />
                  <div className="relative bg-white/[0.02] border border-white/[0.08] rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-brand-blue/20 flex items-center justify-center">
                        <Phone className="w-4 h-4 text-brand-blue" />
                      </div>
                      <span className="text-sm font-semibold text-foreground">Phone OTP Login</span>
                      <span className="ml-auto text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-brand-blue/20 text-brand-blue">Recommended</span>
                    </div>
                    <PhoneOTPLogin onSuccess={handleLoginSuccess} />
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-white/[0.06]" />
                  <span className="text-xs text-muted-foreground/60 uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                </div>

                {/* Google Login */}
                <button
                  type="button"
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/dashboard';
                    window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="w-full flex items-center justify-center gap-3 h-12 bg-white hover:bg-gray-50 text-gray-800 font-semibold rounded-xl transition-colors border border-gray-200"
                  data-testid="google-login-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                  Continue with Google
                </button>

                {/* SECONDARY: Email Login (collapsible) */}
                <div data-testid="email-login-section">
                  <button
                    type="button"
                    onClick={() => setShowEmailLogin(!showEmailLogin)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] transition-colors"
                    data-testid="email-login-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground font-medium">Login with Email & Password</span>
                    </div>
                    {showEmailLogin ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  {showEmailLogin && (
                    <div className="mt-4 animate-in">
                      <EmailLogin onSuccess={handleLoginSuccess} onStatusChange={setAccessStatus} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* OTP NOT ENABLED: Google + Email login */
              <>
                {/* Google Login — Primary */}
                <button
                  type="button"
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/dashboard';
                    window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="w-full flex items-center justify-center gap-3 h-12 bg-white hover:bg-gray-50 text-gray-800 font-semibold rounded-xl transition-colors border border-gray-200"
                  data-testid="google-login-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                  Continue with Google
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-white/[0.06]" />
                  <span className="text-xs text-muted-foreground/60 uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                </div>

                {/* Email Login — Secondary */}
                <EmailLogin onSuccess={handleLoginSuccess} onStatusChange={setAccessStatus} />
              </>
            )}

            {/* Sign up link */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Don&apos;t have an account?{" "}
                <Link to="/sign-up" className="text-brand-blue hover:underline font-bold" data-testid="register-link">
                  Create your free account
                </Link>
              </p>
            </div>

            <div className="divider-gradient" />
            <div className="text-center">
              <Link to="/admin-login" className="text-xs text-muted-foreground hover:text-brand-blue transition-colors" data-testid="admin-login-link">
                Admin Login
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Branding Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            Tracked with <span className="gradient-text font-medium">FitTrack Coach</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <a href="https://app.fittrackcoach.in" target="_blank" rel="noopener noreferrer" className="hover:text-brand-blue transition-colors">app.fittrackcoach.in</a>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">by InfiNest Tech Pvt. Ltd.</p>
        </div>
      </div>
    </div>
  );
}
